import { type ChainflipAsset } from '@chainflip/utils/chainflip';
import { isNotNullish } from '@chainflip/utils/guard';
import ExpandableContainer from '@/shared/components/ExpandableContainer';
import QuestionMarkTooltip from '@/shared/components/QuestionMarkTooltip';
import useChainflipAssetPrices from '@/shared/hooks/useChainflipAssetPrices';
import ArrowIcon from '@/shared/icons/flip-ui-kit/small/Arrow';
import { apyToText } from '@/shared/utils/boost';
import { formatUsdValue } from '@/shared/utils/numbers';
import {
  calculateAnnualReturn,
  formatStablecoinStrategyRange,
  TOOLTIP_COPY,
  type AllowedStrategyType,
} from '@/shared/utils/strategies';
import type TokenAmount from '@/shared/utils/TokenAmount';

export const NewStrategyOverview = ({
  type,
  baseAsset,
  quoteAsset,
  baseAssetAmount,
  quoteAssetAmount,
  minBuy,
  maxBuy,
  minSell,
  maxSell,
  apyPercent,
}: {
  type: AllowedStrategyType;
  baseAsset?: ChainflipAsset;
  quoteAsset: ChainflipAsset;
  baseAssetAmount?: TokenAmount;
  quoteAssetAmount?: TokenAmount;
  minBuy?: string;
  maxBuy?: string;
  minSell?: string;
  maxSell?: string;
  apyPercent?: number;
}) => {
  const { prices: assetPrices } = useChainflipAssetPrices();

  const baseLiquidityUsd =
    baseAsset && assetPrices[baseAsset]
      ? (baseAssetAmount?.mul(assetPrices[baseAsset]).toNumber() ?? 0)
      : 0;
  const quoteLiquidityUsd =
    quoteAsset && assetPrices[quoteAsset]
      ? (quoteAssetAmount?.mul(assetPrices[quoteAsset]).toNumber() ?? 0)
      : 0;
  const totalLiquidityUsd = baseLiquidityUsd + quoteLiquidityUsd;
  const projectedAnnualReturn = apyPercent && calculateAnnualReturn(totalLiquidityUsd, apyPercent);

  const apy =
    apyPercent !== undefined ? (
      <div className="text-cf-white">
        {apyToText(apyPercent)}
        {projectedAnnualReturn !== undefined && projectedAnnualReturn > 0 && (
          <span className="text-cf-green-3">
            {' '}
            (+{formatUsdValue(projectedAnnualReturn, undefined, true)})
          </span>
        )}
      </div>
    ) : (
      <div>N/A</div>
    );

  const label = type === 'INVENTORY_BASED' ? 'Price' : 'Offset';

  return (
    <ExpandableContainer
      label={<span className="flex gap-x-1">Estimated APY: {apy}</span>}
      displayShowHideLabel={false}
      className="bg-cf-gray-3-5"
    >
      <div className="flex flex-col gap-y-3 rounded-md border border-cf-gray-4 bg-cf-gray-3-5 p-4 font-aeonikMedium text-14 text-cf-light-2 shadow-grayPop3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-x-1">
            <ArrowIcon direction="down" />
            <span>Buy {label}</span>
            <QuestionMarkTooltip content={TOOLTIP_COPY[type].buy} />
          </div>
          {minBuy !== '' &&
          maxBuy !== '' &&
          isNotNullish(minBuy) &&
          isNotNullish(maxBuy) &&
          isNotNullish(baseAsset) ? (
            <div className="flex items-center gap-x-1">
              {formatStablecoinStrategyRange(minBuy, maxBuy, type)}
            </div>
          ) : (
            <div>N/A</div>
          )}
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-x-1">
            <ArrowIcon direction="up" />
            <span>Sell {label}</span>
            <QuestionMarkTooltip content={TOOLTIP_COPY[type].sell} />
          </div>
          {minSell !== '' &&
          maxSell !== '' &&
          isNotNullish(minSell) &&
          isNotNullish(maxSell) &&
          isNotNullish(baseAsset) ? (
            <div className="flex items-center gap-x-1">
              {formatStablecoinStrategyRange(minSell, maxSell, type)}
            </div>
          ) : (
            <div>N/A</div>
          )}
        </div>
      </div>
    </ExpandableContainer>
  );
};
